<template>
    <div v-show="showSurvivabilitySubSection">
        <p>On average you may be expected to live to <span class="fw-bold">{{ ageAtDeathOn50thPercentile }}</span>.</p>
        <p>We are required to build you a plan which is expected to last longer than this.</p>
        <p>There is currently a 25% chance of you living to <span class="fw-bold">{{ ageAtDeathOnDesignatedPercentile }}</span>.</p>
        <p>Therefore we will build you a plan to expect to last to then. This is <span class="fw-bold">{{ yearsToLiveFromRetirementOnDesignatedPercentile }}</span> years from your retirement date.</p>

        <div>
            <p>
                Life expectancy is complex. The age you are expected to survive to increases as you get older, as you have
                already made it so far. Therefore it's worth checking this every few years to see if it has increased a little.
                <a href="https://www.ons.gov.uk/peoplepopulationandcommunity/birthsdeathsandmarriages/lifeexpectancies" target="_blank">
                    Find out more about life expectancy from the ONS.
                </a>
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, onMounted } from "vue";
    import { useStore } from "vuex";
    import { app } from "@/guiide/ts/app";
    import { getAgeAtRetirement } from "@/guiide/ts/utilities";

    const emit = defineEmits(['complete']);

    const store = useStore();
    const yearsToLiveFromRetirementOn50thPercentile = ref(0);

    onMounted(async () => {
        await getYearsToLiveFromRetirementOnAverage();

        if (store.state.retiree?.survivalPercentile != null) {
            emit("complete");
        }
    });

    watch(
        () => store.state.retiree,
        async (newRetiree) => {
            if (newRetiree == null) {
                return;
            }

            if (newRetiree.survivalPercentile != null) {
                emit("complete");
            } else {
                updateSurvivability();
            }
        },
        { immediate: true }
    );

    const ageAtRetirement = computed<number>(() => getAgeAtRetirement() ?? 0);
    const ageAtDeathOn50thPercentile = computed<number>(() => yearsToLiveFromRetirementOn50thPercentile.value + ageAtRetirement.value);
    const yearsToLiveFromRetirementOnDesignatedPercentile = computed<number>(() => store.state.retiree?.lifeExpectancy ?? 0);
    const ageAtDeathOnDesignatedPercentile = computed<number>(() => yearsToLiveFromRetirementOnDesignatedPercentile.value + ageAtRetirement.value);

    const showSurvivabilitySubSection = computed<boolean>(() => ageAtRetirement.value > 0 && survivalPercentileHasBeenSet.value && averageLifeExpectancyLoaded.value);
    const survivalPercentileHasBeenSet = computed<boolean>(() => store.state.retiree?.survivalPercentile != null);
    const averageLifeExpectancyLoaded = computed<boolean>(() => yearsToLiveFromRetirementOn50thPercentile.value != 0 );

    async function updateSurvivability() {
        if (survivalPercentileHasBeenSet.value) {
            return;
        }

        await app.updateSurvivabilityState();
    }

    async function getYearsToLiveFromRetirementOnAverage() {
        if (averageLifeExpectancyLoaded.value) {
            return;
        }

        const average = await app.getYearsToLiveFromRetirementOn50thSurvivalPercentile();
        yearsToLiveFromRetirementOn50thPercentile.value = average;
    }
</script>
